import {AgendaSearchParams} from '@/features/agenda/types'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {useCallback, useEffect, useMemo} from 'react'
import {useAgendaNavigation} from './useAgendaNavigation'

export const useAgendaSearchParams = () => {
    const {
        navigateToView,
        params: {view}
    } = useAgendaNavigation()
    const {searchParams, setSearchParams} = useParsedSearchParams(AgendaSearchParams, [
        'subjectIds',
        'specializationIds'
    ])

    const clearSearchParams = useCallback(() => {
        setSearchParams({
            subjectIds: undefined,
            specializationIds: undefined,
            category: undefined,
            patientId: undefined
        })
    }, [setSearchParams])

    const isSubjectSelected = useCallback(
        (subjectId: string) => {
            return searchParams.subjectIds?.includes(subjectId)
        },
        [searchParams.subjectIds]
    )

    const isPatientAgeRangeCategory = useMemo(() => {
        return (
            searchParams.category == 'evolutionary_age' ||
            searchParams.category == 'evolutionary_age_adults' ||
            searchParams.category == 'adults'
        )
    }, [searchParams.category])

    const isTeamCategory = useMemo(() => {
        return searchParams.category == 'team'
    }, [searchParams.category])

    const isCustomCategory = useMemo(() => {
        return searchParams.category == 'custom'
    }, [searchParams.category])

    useEffect(() => {
        // The day view is the only one able to render several subjects side by side, so we force it.
        // Bail out when we already are on it: `searchParams` is re-parsed on every render, so this
        // effect re-runs continuously and navigating to the very same url keeps pushing history
        // entries, remounting the calendar and wiping any open event dialog.
        if (view == 'day') return

        if (
            (searchParams.subjectIds && searchParams.subjectIds.length > 1) ||
            (isTeamCategory && !searchParams.subjectIds) ||
            (isTeamCategory && searchParams.subjectIds && searchParams.subjectIds.length > 1)
        ) {
            navigateToView('day')
        }
    }, [searchParams.subjectIds, isTeamCategory, navigateToView, view])

    return {
        searchParams,
        setSearchParams,
        isSubjectSelected,
        isPatientAgeRangeCategory,
        isTeamCategory,
        isCustomCategory,
        clearSearchParams
    }
}
